import type { DataColumn, ModelKey, MetricSection } from "../types";
import { DATE_SQL_TYPES, columnDataTypeCategory } from "./operatorCatalog";
import type { ReportLogicContext } from "./context";
import { needsRelatedPredicate, relatedExistsSql, qualifiedRelatedPredicate, relatedTableKey, qualifiedConditionColumn } from "./relatedTables";
import { resolveOptionIds } from "./lookupResolution";
import { normalizeReportingPeriodType } from "./reportingPeriods";
import { findTimestampColumn, columnsForModel } from "./catalogResolution";
import {
    effectiveComparisonTier,
    truncateColumnToTier,
    differenceTruncationTier,
    resolveDifferenceUnit,
    TIME_DIFFERENCE_UNIT_SQL,
} from "./temporalPrecision";
import type { TemporalTier, TimeDifferenceUnit } from "./temporalPrecision";

function normalizedSqlDataType(column?: DataColumn | null) {
    return String(column?.dataType || "").trim().toLowerCase().replace(/\(.*$/, "");
}

function isTextSqlDataType(dataType: string) {
    return dataType.includes("char") || dataType.includes("text") || dataType.includes("string");
}

function isNativeDateColumn(column?: DataColumn | null) {
    const dataType = normalizedSqlDataType(column);
    return !!dataType && !isTextSqlDataType(dataType) && DATE_SQL_TYPES.includes(dataType);
}

function isDateLikeColumn(column?: DataColumn | null) {
    return !!column?.isPeriodAnchor || columnDataTypeCategory(column) === "date";
}

function dateSqlForColumn(column: DataColumn) {
    if (isNativeDateColumn(column)) return column.sql;
    const textValue = `NULLIF(LTRIM(RTRIM(CONVERT(NVARCHAR(50), ${column.sql}))), '')`;
    return `COALESCE(TRY_CONVERT(datetime2, ${textValue}, 126), TRY_CONVERT(datetime2, ${textValue}, 120), TRY_CONVERT(datetime2, ${textValue}, 101), TRY_CONVERT(datetime2, ${textValue}, 110), TRY_CONVERT(datetime2, ${textValue}))`;
}

function comparisonSqlForColumn(column: DataColumn) {
    return isDateLikeColumn(column) ? dateSqlForColumn(column) : column.sql;
}

/**
 * The whole-minute difference between two timestamp columns.
 *
 * Both operands go through dateSqlForColumn, so a text-backed date column gets the
 * TRY_CONVERT ladder rather than being handed to DATEDIFF as text - one malformed source
 * value then yields NULL for that row instead of failing the whole query.
 *
 * DATEDIFF(MINUTE, ...) counts BOUNDARY CROSSINGS, not elapsed time: 10:00:59 -> 10:01:00
 * is 1, not 0.017. DATEDIFF(SECOND, ...) / 60.0 would be the more literal "minutes
 * between", and greenfield it would be the right choice. It is not the right choice here:
 * buildLateConditionSql already defines OTP lateness with DATEDIFF(MINUTE, ...), and a
 * second, subtly different definition of "minutes" would have a categorization rule and an
 * OTP metric disagree about the same two timestamps on the same row. One definition,
 * consistently applied, beats two that are each defensible. The error is bounded under a
 * minute and the builder's hint text says so.
 *
 * Mirrors MinutesBetweenSql in ReportSqlBuilder.cs - the two must stay identical.
 */
function minutesBetweenSql(
    fromColumn: DataColumn,
    toColumn: DataColumn,
    absolute?: boolean,
    requestedUnit?: TimeDifferenceUnit | null,
) {
    // Where the two columns differ in precision, BOTH operands are truncated to the coarser
    // one before the difference is taken. Without this, DATEDIFF over a `date` and a
    // `datetime2` measures from a midnight the date column never contained, and the unit
    // resolution below has already refused to express such a pair in minutes.
    const tier = differenceTruncationTier(fromColumn, toColumn);
    const operand = (column: DataColumn) => {
        const sql = dateSqlForColumn(column);
        return tier ? truncateColumnToTier(sql, column, tier) : sql;
    };
    const unit = resolveDifferenceUnit(fromColumn, toColumn, requestedUnit);
    const diff = `DATEDIFF(${TIME_DIFFERENCE_UNIT_SQL[unit]}, ${operand(fromColumn)}, ${operand(toColumn)})`;
    return absolute ? `ABS(${diff})` : diff;
}

/**
 * A comparison whose right-hand side is another column rather than a literal.
 *
 * Both sides go through comparisonSqlForColumn so a text-backed date is converted on BOTH
 * sides rather than compared as text. Returns null for an operator that has no reading
 * against a single column - the list, text and blank operators - so the caller drops the
 * condition instead of emitting something that silently never matches.
 *
 * "ne" carries the NULL arms for the same reason the single-value "ne" already does: a
 * comparison involving NULL is UNKNOWN, and a row where either side is NULL plainly does
 * not satisfy "these two are equal", so it belongs in the "not equal" result.
 *
 * Mirrors ColumnComparisonSql in ReportSqlBuilder.cs.
 */
function columnComparisonSql(
    left: DataColumn,
    right: DataColumn,
    operator: string,
    granularity?: TemporalTier | "exact" | null,
    fallback?: NullFallback | null,
) {
    // Where the two columns differ in precision, both sides are truncated to the coarser of
    // the two rather than being handed to SQL Server to widen implicitly - which supplies a
    // midnight for a `date` column and makes the comparison true only at 00:00:00.000.
    // effectiveComparisonTier returns null for two columns of the same precision, so such a
    // comparison is byte-identical to what it was before precision handling existed.
    const tier = effectiveComparisonTier(left, right, granularity);
    // fallback is the "If blank, use" value: ISNULL/COALESCE(left, value).
    const leftSql = leftComparisonSql(left, fallback || null);
    const l = tier ? truncateColumnToTier(leftSql, left, tier) : leftSql;
    const r = tier ? truncateColumnToTier(comparisonSqlForColumn(right), right, tier) : comparisonSqlForColumn(right);
    switch (operator) {
        case "eq": return `${l} = ${r}`;
        case "ne": return `(${l} IS NULL OR ${r} IS NULL OR ${l} <> ${r})`;
        case "gt": case "after": return `${l} > ${r}`;
        case "gte": return `${l} >= ${r}`;
        case "lt": case "before": return `${l} < ${r}`;
        case "lte": return `${l} <= ${r}`;
        default: return null;
    }
}

/** The operators that have a meaning when the right-hand side is a column. */
const COLUMN_COMPARISON_OPERATORS = ["eq", "ne", "gt", "gte", "lt", "lte", "before", "after"];

// --- "Compare To Today" and "If blank, use <value>" --------------------------------------
// Mirrors ReportSqlBuilder.DateOperands.cs and ReportSqlBuilder.NullFallback.cs - keep them in
// step. The server binds the fallback as a typed parameter; the preview shows a typed literal.

/** The run date, as compared by "Compare To Today" and used by period bounds. Mirrors ReportSqlBuilder.TodaySql. */
const TODAY_SQL = "CAST(GETDATE() AS date)";

/** The date suggested when "If blank, use" is switched on for a date column (open-ended). */
const DEFAULT_BLANK_DATE = "2199-12-31";

/** The operators "Compare To Today" offers - the same as a column comparison. */
const TODAY_COMPARISON_OPERATORS = COLUMN_COMPARISON_OPERATORS;

/** The operators the legacy date-only blankDate applied to. */
const BLANK_DATE_OPERATORS = [...COLUMN_COMPARISON_OPERATORS, "between", "not_between"];

/** Every value operator: "If blank, use" applies to all of them, but not to the blank checks. */
const NULL_FALLBACK_OPERATORS = [
    "in", "not_in", "eq", "ne", "contains", "not_contains", "starts_with", "not_starts_with",
    "ends_with", "not_ends_with", "gt", "gte", "lt", "lte", "before", "after", "between", "not_between",
];

type NullFallbackKind = "unsupported" | "date" | "integer" | "decimal" | "float" | "bit" | "text";

interface NullFallbackType {
    kind: NullFallbackKind;
    baseType: string;
    length?: number | null;
    precision?: number | null;
    scale?: number | null;
}

/** A fallback that fits its column: the SQL literal, whether to widen with COALESCE, and how read-backs show it. */
interface NullFallback {
    literal: string;
    coalesce: boolean;
    display: string;
}

type FallbackCondition = { leftKind?: string; compareTo?: string; operator: string; blankValue?: string | null; blankDate?: string };

// Bounds as digit strings: bigint's exceed JavaScript's safe integers, and the build targets ES5.
const INTEGER_RANGES: Record<string, [string, string]> = {
    tinyint: ["0", "255"],
    smallint: ["-32768", "32767"],
    int: ["-2147483648", "2147483647"],
    bigint: ["-9223372036854775808", "9223372036854775807"],
};

/** Compares two integers written as digit strings (optional leading "-"), exactly. */
function compareIntegerStrings(a: string, b: string) {
    const negA = a.startsWith("-");
    const negB = b.startsWith("-");
    const magA = a.replace(/^[-+]/, "").replace(/^0+(?=\d)/, "");
    const magB = b.replace(/^[-+]/, "").replace(/^0+(?=\d)/, "");
    const zeroA = /^0+$/.test(magA);
    const zeroB = /^0+$/.test(magB);
    if (zeroA && zeroB) return 0;
    if (negA !== negB && !(zeroA || zeroB)) return negA ? -1 : 1;
    const signA = negA && !zeroA ? -1 : 1;
    const signB = negB && !zeroB ? -1 : 1;
    if (signA !== signB) return signA < signB ? -1 : 1;
    const magnitude = magA.length !== magB.length ? (magA.length < magB.length ? -1 : 1) : magA < magB ? -1 : magA > magB ? 1 : 0;
    return signA * magnitude;
}

/** A real calendar date written yyyy-mm-dd. */
function isIsoDate(value?: string | null) {
    return !!parseIsoDateTime(value) && /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());
}

/** yyyy-mm-dd, optionally followed by hh:mm[:ss], as a checked calendar value. */
function parseIsoDateTime(value?: string | null): { date: string; time: string | null } | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(String(value || "").trim());
    if (!match) return null;
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
    if (match[4] === undefined) return { date: `${match[1]}-${match[2]}-${match[3]}`, time: null };
    const [hours, minutes, seconds] = [Number(match[4]), Number(match[5]), Number(match[6] || 0)];
    if (hours > 23 || minutes > 59 || seconds > 59) return null;
    const time = `${match[4]}:${match[5]}:${String(seconds).padStart(2, "0")}`;
    return { date: `${match[1]}-${match[2]}-${match[3]}`, time: time === "00:00:00" ? null : time };
}

function baseTypeName(dataType?: string | null) {
    return String(dataType || "").trim().toLowerCase().replace(/\(.*$/, "").trim();
}

/** The numeric arguments of "varchar(50)" / "decimal(9, 2)"; "max" or anything odd yields none. */
function typeArguments(dataType?: string | null): number[] {
    const match = /\(([^)]*)\)/.exec(String(dataType || ""));
    if (!match) return [];
    const parts = match[1].split(",").map((part) => part.trim());
    return parts.every((part) => /^\d+$/.test(part)) ? parts.map(Number) : [];
}

/**
 * The fallback's type family, from the column's database type (mirrors NullFallbackTypeOf):
 * time-of-day is refused, anything date-like takes a date, then the SQL type decides, a blank
 * type falls back to the category, and anything else is refused rather than guessed.
 */
function nullFallbackType(column?: DataColumn | null): NullFallbackType {
    const baseType = baseTypeName(column?.dataType);
    const args = typeArguments(column?.dataType);
    if (!column || baseType === "time") return { kind: "unsupported", baseType };
    if (isDateLikeColumn(column)) return { kind: "date", baseType };
    if (INTEGER_RANGES[baseType]) return { kind: "integer", baseType };
    if (baseType === "decimal" || baseType === "numeric") {
        return { kind: "decimal", baseType, precision: args.length ? args[0] : 18, scale: args.length > 1 ? args[1] : args.length === 1 ? 0 : null };
    }
    if (baseType === "money") return { kind: "decimal", baseType, precision: 19, scale: 4 };
    if (baseType === "smallmoney") return { kind: "decimal", baseType, precision: 10, scale: 4 };
    if (baseType === "float" || baseType === "real") return { kind: "float", baseType };
    if (baseType === "bit") return { kind: "bit", baseType };
    if (baseType && isTextSqlDataType(baseType)) return { kind: "text", baseType, length: args.length ? args[0] : null };
    if (!baseType) {
        const category = String(column.dataTypeCategory || "").trim().toLowerCase();
        if (category.startsWith("num") || ["int", "integer", "decimal", "money"].includes(category)) return { kind: "decimal", baseType };
        if (category.startsWith("text") || category.startsWith("str") || category.startsWith("char")) return { kind: "text", baseType };
    }
    return { kind: "unsupported", baseType };
}

/** The condition's fallback as typed: blankValue, else the legacy blankDate. Undefined = none. */
function conditionBlankValue(condition: FallbackCondition): string | undefined {
    if (condition.blankValue !== undefined && condition.blankValue !== null) return condition.blankValue;
    return condition.blankDate !== undefined && condition.blankDate !== null ? condition.blankDate : undefined;
}

/** The raw fallback when it takes part in this condition's SQL (mirrors NullFallbackRaw), otherwise null. */
function nullFallbackRaw(condition: FallbackCondition, column?: DataColumn | null): string | null {
    if (condition.leftKind === "minutes_between") return null;
    if ((condition.compareTo === "column" || condition.compareTo === "today") && !COLUMN_COMPARISON_OPERATORS.includes(condition.operator)) return null;
    if (condition.blankValue !== undefined && condition.blankValue !== null) {
        return NULL_FALLBACK_OPERATORS.includes(condition.operator) ? condition.blankValue : null;
    }
    const legacy = String(condition.blankDate || "").trim();
    return legacy && isDateLikeColumn(column) && BLANK_DATE_OPERATORS.includes(condition.operator) ? condition.blankDate! : null;
}

function dateDisplay(date: string, time: string | null) {
    const [year, month, day] = date.split("-");
    if (!time) return `${month}-${day}-${year}`;
    const [hours, minutes] = time.split(":").map(Number);
    const twelve = hours % 12 === 0 ? 12 : hours % 12;
    return `${month}-${day}-${year} ${String(twelve).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}`;
}

/** Parses a fallback against the column's type (mirrors ParseNullFallback): the fallback, or why it does not fit. */
function parseNullFallback(column: DataColumn | null | undefined, raw: string, legacyDate: boolean): { fallback: NullFallback } | { problem: string } {
    const type = nullFallbackType(column);
    const label = column?.label || "this column";
    const trimmed = raw.trim();
    switch (type.kind) {
        case "date": {
            const parsed = parseIsoDateTime(trimmed);
            if (!parsed) return { problem: "needs a date." };
            if (legacyDate || type.baseType === "date") {
                if (!legacyDate && parsed.time) return { problem: `${label} holds dates without a time of day, so the fallback cannot have one.` };
                return { fallback: { literal: `CAST(${sqlLiteral(parsed.date)} AS date)`, coalesce: false, display: dateDisplay(parsed.date, null) } };
            }
            return parsed.time
                ? { fallback: { literal: `CAST(${sqlLiteral(`${parsed.date}T${parsed.time}`)} AS datetime2)`, coalesce: false, display: dateDisplay(parsed.date, parsed.time) } }
                : { fallback: { literal: `CAST(${sqlLiteral(parsed.date)} AS date)`, coalesce: false, display: dateDisplay(parsed.date, null) } };
        }
        case "integer": {
            if (!/^[+-]?\d+$/.test(trimmed)) return { problem: `${label} holds whole numbers, so the fallback must be a whole number.` };
            const [min, max] = INTEGER_RANGES[type.baseType];
            const sign = trimmed.startsWith("-") ? "-" : "";
            const digits = trimmed.replace(/^[-+]/, "").replace(/^0+(?=\d)/, "");
            const number = digits === "0" ? "0" : `${sign}${digits}`;
            if (compareIntegerStrings(number, min) < 0 || compareIntegerStrings(number, max) > 0) return { problem: `must be between ${min} and ${max} for a ${type.baseType} column.` };
            return { fallback: { literal: number, coalesce: false, display: number } };
        }
        case "decimal": {
            if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(trimmed)) return { problem: `${label} holds numbers, so the fallback must be a number.` };
            let coalesce = true;
            if (typeof type.precision === "number" && typeof type.scale === "number") {
                const digits = trimmed.replace(/^[+-]/, "");
                const point = digits.indexOf(".");
                const fraction = point < 0 ? 0 : digits.length - point - 1;
                const whole = (point < 0 ? digits : digits.slice(0, point)).replace(/^0+/, "").length;
                if (fraction > type.scale) return { problem: `has ${fraction} decimal places, but ${label} (${type.baseType}(${type.precision},${type.scale})) keeps ${type.scale}.` };
                if (whole > type.precision - type.scale) return { problem: `is too large for ${label} (${type.baseType}(${type.precision},${type.scale})).` };
                coalesce = false;
            }
            return { fallback: { literal: trimmed, coalesce, display: trimmed } };
        }
        case "float": {
            if (!/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(trimmed) || !Number.isFinite(Number(trimmed))) return { problem: `${label} holds numbers, so the fallback must be a number.` };
            return { fallback: { literal: trimmed, coalesce: false, display: trimmed } };
        }
        case "bit": {
            const word = trimmed.toLowerCase();
            if (["1", "true", "yes", "y"].includes(word)) return { fallback: { literal: "1", coalesce: false, display: "Yes" } };
            if (["0", "false", "no", "n"].includes(word)) return { fallback: { literal: "0", coalesce: false, display: "No" } };
            return { problem: `${label} is a yes/no column, so the fallback must be Yes or No.` };
        }
        case "text": {
            if (typeof type.length === "number" && raw.length > type.length) return { problem: `is ${raw.length} characters, but ${label} holds at most ${type.length}.` };
            return { fallback: { literal: `N${sqlLiteral(raw)}`, coalesce: true, display: raw.length ? `"${raw}"` : "empty text" } };
        }
        default:
            return { problem: column?.dataType ? `a fallback cannot be used on a ${column.dataType} column.` : `the catalog does not say what type ${label} holds, so a fallback cannot be checked.` };
    }
}

/** The fallback when it takes part in this condition's SQL AND fits the column, otherwise null. */
function nullFallbackFor(condition: FallbackCondition, column?: DataColumn | null): NullFallback | null {
    const raw = nullFallbackRaw(condition, column);
    if (raw === null) return null;
    const result = parseNullFallback(column, raw, condition.blankValue === undefined || condition.blankValue === null);
    return "fallback" in result ? result.fallback : null;
}

/** Why the condition's fallback does not fit its column, or null when it fits or does not apply. */
function nullFallbackIssue(condition: FallbackCondition, column?: DataColumn | null): string | null {
    const raw = nullFallbackRaw(condition, column);
    if (raw === null) return null;
    const result = parseNullFallback(column, raw, condition.blankValue === undefined || condition.blankValue === null);
    return "problem" in result ? `"If blank, use" value "${raw}" for ${column?.label || "this column"}: ${result.problem}` : null;
}

/** True when the editor should offer "If blank, use" for this condition/column/operator. */
function nullFallbackSupported(condition: { leftKind?: string; compareTo?: string; operator: string }, column?: DataColumn | null) {
    if (condition.leftKind === "minutes_between" || !column) return false;
    const operators = condition.compareTo === "column" || condition.compareTo === "today" ? COLUMN_COMPARISON_OPERATORS : NULL_FALLBACK_OPERATORS;
    return operators.includes(condition.operator) && nullFallbackType(column).kind !== "unsupported";
}

function wrapNullFallback(sql: string, fallback: NullFallback | null) {
    if (!fallback) return sql;
    return fallback.coalesce ? `COALESCE(${sql}, ${fallback.literal})` : `ISNULL(${sql}, ${fallback.literal})`;
}

/** The left side of a comparison: the column, wrapped in its fallback when one applies. */
function leftComparisonSql(column: DataColumn, fallback: NullFallback | null) {
    return wrapNullFallback(comparisonSqlForColumn(column), fallback);
}

/** "<column> <op> today", compared by day. Null for an operator with no reading against one date. */
function todayComparisonSql(column: DataColumn, operator: string, fallback: NullFallback | null) {
    const l = truncateColumnToTier(leftComparisonSql(column, fallback), column, "day");
    switch (operator) {
        case "eq": return `${l} = ${TODAY_SQL}`;
        case "ne": return `(${l} IS NULL OR ${l} <> ${TODAY_SQL})`;
        case "gt": case "after": return `${l} > ${TODAY_SQL}`;
        case "gte": return `${l} >= ${TODAY_SQL}`;
        case "lt": case "before": return `${l} < ${TODAY_SQL}`;
        case "lte": return `${l} <= ${TODAY_SQL}`;
        default: return null;
    }
}

/**
 * Date wording for "today" in read-backs - "is today or later" rather than "is today or more".
 * form "is": English sentence; "bare": after "needed:". Mirrors TodayPhrase in
 * ReportSqlBuilder.DateOperands.cs.
 */
function todayPhrase(operator: string, form: "is" | "bare") {
    const bare = operator === "eq" ? "today"
        : operator === "ne" ? "not today"
            : operator === "gt" || operator === "after" ? "after today"
                : operator === "gte" ? "today or later"
                    : operator === "lt" || operator === "before" ? "before today"
                        : operator === "lte" ? "today or earlier" : "today";
    if (form === "bare") return bare;
    return operator === "ne" ? "is not today" : `is ${bare}`;
}

/** " (blank counts as 12-31-2199)" for read-backs, or "" when no valid fallback applies (mirrors NullFallbackNote). */
function nullFallbackNote(condition: FallbackCondition, column?: DataColumn | null) {
    const fallback = nullFallbackFor(condition, column);
    return fallback ? ` (blank counts as ${fallback.display})` : "";
}

/** The operators that have a meaning against a whole-minute difference (a number). */
const MINUTES_BETWEEN_OPERATORS = ["eq", "ne", "gt", "gte", "lt", "lte", "between", "not_between"];

function countAllExpr(column: DataColumn | null) {
    return column ? `COUNT(DISTINCT ${column.sql})` : "COUNT(*)";
}

function countMatchingExpr(column: DataColumn | null, condition: string) {
    return column ? `COUNT(DISTINCT CASE WHEN ${condition} THEN ${column.sql} END)` : `SUM(CASE WHEN ${condition} THEN 1 ELSE 0 END)`;
}

function sqlList(values: string[]) {
    return values.map(sqlLiteral).join(", ");
}

function sqlLiteral(value: string) {
    return `'${String(value).replace(/'/g, "''")}'`;
}

function dateSqlLiteral(value: string) {
    const literal = sqlLiteral(value);
    return `COALESCE(TRY_CONVERT(datetime2, ${literal}, 126), TRY_CONVERT(datetime2, ${literal}, 120), TRY_CONVERT(datetime2, ${literal}, 101), TRY_CONVERT(datetime2, ${literal}, 110), TRY_CONVERT(datetime2, ${literal}))`;
}

function sqlListForColumn(column: DataColumn, values: string[]) {
    return values.map((value) => isDateLikeColumn(column) ? dateSqlLiteral(value) : sqlLiteral(value)).join(", ");
}

function nullSafeNotIn(columnSql: string, values: string[]) {
    return `(${columnSql} IS NULL OR ${columnSql} NOT IN (${sqlList(values)}))`;
}

function nullSafeNotInRaw(columnSql: string, valueListSql: string) {
    return `(${columnSql} IS NULL OR ${columnSql} NOT IN (${valueListSql}))`;
}

function idSqlList(ids: string[]) {
    return ids.map((id) => (/^-?\d+$/.test(id) ? id : sqlLiteral(id))).join(", ");
}

// leftSql is the "If blank, use" fallback, ISNULL/COALESCE(column, value); it bypasses the ID
// shortcut, which compares a different column the fallback was not checked against.
function inClause(context: ReportLogicContext, modelKey: ModelKey | "", column: DataColumn, values: string[], useIds: boolean, leftSql?: string | null): string {
    // A related column (logic/relatedTables.ts): "a related row is one of".
    if (needsRelatedPredicate(column)) {
        return relatedExistsSql(column, qualifiedRelatedPredicate(relatedTableKey(column), () => inClause(context, modelKey, qualifiedConditionColumn(column), values, false, leftSql)), false);
    }
    if (leftSql) return `${leftSql} IN (${sqlListForColumn(column, values)})`;
    if (useIds && column.idSql) {
        const ids = resolveOptionIds(context, modelKey, column.key, values);
        if (ids) return `${column.idSql} IN (${idSqlList(ids)})`;
    }
    return `${comparisonSqlForColumn(column)} IN (${sqlListForColumn(column, values)})`;
}

function notInClause(context: ReportLogicContext, modelKey: ModelKey | "", column: DataColumn, values: string[], useIds: boolean, leftSql?: string | null): string {
    // A related column: "no related row is one of".
    if (needsRelatedPredicate(column)) {
        return relatedExistsSql(column, qualifiedRelatedPredicate(relatedTableKey(column), () => inClause(context, modelKey, qualifiedConditionColumn(column), values, false, leftSql)), true);
    }
    if (leftSql) return `(${leftSql} IS NULL OR ${leftSql} NOT IN (${sqlListForColumn(column, values)}))`;
    if (useIds && column.idSql) {
        const ids = resolveOptionIds(context, modelKey, column.key, values);
        if (ids) return nullSafeNotInRaw(column.idSql, idSqlList(ids));
    }
    const columnSql = comparisonSqlForColumn(column);
    return `(${columnSql} IS NULL OR ${columnSql} NOT IN (${sqlListForColumn(column, values)}))`;
}

function sqlLikeLiteral(value: string, mode: "contains" | "starts_with" | "ends_with") {
    const escaped = String(value)
        .replace(/\\/g, "\\\\")
        .replace(/'/g, "''")
        .replace(/%/g, "\\%")
        .replace(/_/g, "\\_")
        .replace(/\[/g, "\\[");
    const pattern = mode === "contains" ? `%${escaped}%` : mode === "starts_with" ? `${escaped}%` : `%${escaped}`;
    return `'${pattern}' ESCAPE '\\'`;
}

function sqlAlias(alias: string) {
    return `[${String(alias || "Value").replace(/]/g, "]]")}]`;
}

function safeSqlAlias(value: string) {
    const alias = String(value || "Value").replace(/[^a-zA-Z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
    return alias || "Value";
}

function indentSql(sql: string, spaces = 4) {
    const padding = " ".repeat(spaces);
    return sql.split("\n").map((line) => `${padding}${line}`).join("\n");
}

function periodSqlClause(type: string, column: string, customStart: string, customEnd: string, lastN?: number) {
    const periodType = normalizeReportingPeriodType(type);
    const rangeClause = (start: string, end: string) => `(${column} >= ${start}\n  AND ${column} < ${end})`;
    const inclusiveRangeClause = (start: string, end: string) => `(${column} >= ${start}\n  AND ${column} <= ${end})`;
    if (periodType === "PreviousMonth") return rangeClause("DATEADD(MONTH, DATEDIFF(MONTH,0,GETDATE())-1, 0)", "DATEADD(MONTH, DATEDIFF(MONTH,0,GETDATE()), 0)");
    if (periodType === "CurrentMonthToDate") return inclusiveRangeClause("DATEADD(MONTH, DATEDIFF(MONTH,0,GETDATE()), 0)", "GETDATE()");
    if (periodType === "Last7Days") return `${column} >= DATEADD(DAY,-7,GETDATE())`;
    if (periodType === "Last30Days") return `${column} >= DATEADD(DAY,-30,GETDATE())`;
    if (periodType === "CurrentQuarter") return `${column} >= DATEADD(QUARTER, DATEDIFF(QUARTER,0,GETDATE()), 0)`;
    if (periodType === "PreviousQuarter") return rangeClause("DATEADD(QUARTER, DATEDIFF(QUARTER,0,GETDATE())-1, 0)", "DATEADD(QUARTER, DATEDIFF(QUARTER,0,GETDATE()), 0)");
    if (periodType === "YearToDate") return inclusiveRangeClause("DATEFROMPARTS(YEAR(GETDATE()),1,1)", "GETDATE()");
    // N is range-checked by validation; the preview shows N when it is not set yet.
    // N = 0: today (since midnight) / the current month to date. Mirrors ReportSqlBuilder.
    const n = typeof lastN === "number" && Number.isInteger(lastN) && lastN >= 0 ? String(lastN) : "N";
    if (periodType === "LastNDays" && n === "0") return `${column} >= CAST(CAST(GETDATE() AS date) AS datetime)`;
    if (periodType === "LastNMonths" && n === "0") return inclusiveRangeClause("DATEADD(MONTH, DATEDIFF(MONTH,0,GETDATE()), 0)", "GETDATE()");
    if (periodType === "LastNDays") return `${column} >= DATEADD(DAY,-${n},GETDATE())`;
    if (periodType === "LastNMonths") return rangeClause(`DATEADD(MONTH, DATEDIFF(MONTH,0,GETDATE())-${n}, 0)`, "DATEADD(MONTH, DATEDIFF(MONTH,0,GETDATE()), 0)");
    const start = customStart ? dateSqlLiteral(customStart) : "@StartDate";
    const end = customEnd ? dateSqlLiteral(customEnd) : "@EndDate";
    return rangeClause(start, `DATEADD(DAY, 1, ${end})`);
}

function qualifySqlColumn(sql: string, alias: string) {
    return /^[A-Za-z_][A-Za-z0-9_]*$/.test(sql) ? `${alias}.${sqlAlias(sql)}` : sql;
}

function otpRuleColSql(context: ReportLogicContext, section: MetricSection, key: string) {
    if (!section.model || !key) return null;
    return findTimestampColumn(context.dataModels[section.model], key)?.sql || null;
}

function buildLateConditionSql(context: ReportLogicContext, section: MetricSection, useIds = false) {
    if (!section.model) return null;
    const parts = section.otpRules.filter((rule) => rule.compareCol && rule.againstCol).map((rule) => {
        const compareColumn = otpRuleColSql(context, section, rule.compareCol);
        const againstColumn = otpRuleColSql(context, section, rule.againstCol);
        if (!compareColumn || !againstColumn) return null;
        const diff = `ABS(DATEDIFF(MINUTE, ${againstColumn}, ${compareColumn})) >= ${rule.thresholdMinutes}`;
        const conditions = rule.conditions.filter((condition) => condition.column && condition.values.length).map((condition) => {
            const column = columnsForModel(context, section.model).find((item) => item.key === condition.column);
            return column ? inClause(context, section.model, column, condition.values, useIds) : null;
        }).filter(Boolean) as string[];
        const conditionPart = conditions.length > 1 ? `(${conditions.join(rule.conditionLogic === "or" ? " OR " : " AND ")})` : conditions[0];
        return `(${conditionPart ? `${conditionPart} AND ` : ""}${diff})`;
    }).filter(Boolean) as string[];
    return parts.length ? parts.join("\n    OR ") : null;
}

/**
 * Collapses line breaks so free text (custom condition values, column labels) can never
 * break out of a generated `--` comment and land in the expression itself. Mirrors
 * SanitizeComment in ReportSqlBuilder.cs.
 */
function sqlCommentText(text: string) {
    return String(text || "").replace(/[\r\n]+/g, " ");
}

const RESULT_PLAIN_CHARACTER_TYPES = ["char", "varchar", "nchar", "nvarchar"];
const RESULT_DATETIME_TYPES = ["datetime", "datetime2", "smalldatetime", "datetimeoffset"];

/**
 * A column's value as NVARCHAR text, for a CASE branch that shares the CASE with NVARCHAR
 * labels (a "Set value" rule's THEN, or a rule set's column ELSE).
 *
 * A CASE returns the highest-precedence type among its branches, so one INT / DATE / BIT
 * column next to typed labels makes SQL Server convert every LABEL to that type and fail
 * the query. Plain character columns are returned as they are, so SQL that only ever used
 * text columns is unchanged. A blank dataType is not trusted to mean text.
 *
 * Mirrors ResultTextSql in ReportSqlBuilder.RuleResults.cs - the two must stay identical.
 */
function resultTextSqlForColumn(column: DataColumn) {
    const dataType = normalizedSqlDataType(column);
    const sql = column.sql;
    if (RESULT_PLAIN_CHARACTER_TYPES.includes(dataType)) return sql;
    if (dataType === "bit") return `CASE WHEN ${sql} = 1 THEN N'Yes' WHEN ${sql} = 0 THEN N'No' END`;
    if (dataType === "date") return `CONVERT(NVARCHAR(10), ${sql}, 23)`;
    if (RESULT_DATETIME_TYPES.includes(dataType)) return `CONVERT(NVARCHAR(19), ${sql}, 120)`;
    if (dataType === "time") return `CONVERT(NVARCHAR(8), ${sql}, 108)`;
    return `CONVERT(NVARCHAR(4000), ${sql})`;
}

export {
    resultTextSqlForColumn,
    normalizedSqlDataType,
    isTextSqlDataType,
    isNativeDateColumn,
    isDateLikeColumn,
    dateSqlForColumn,
    comparisonSqlForColumn,
    countAllExpr,
    countMatchingExpr,
    sqlList,
    sqlLiteral,
    dateSqlLiteral,
    sqlListForColumn,
    nullSafeNotIn,
    nullSafeNotInRaw,
    idSqlList,
    inClause,
    notInClause,
    sqlLikeLiteral,
    sqlAlias,
    safeSqlAlias,
    indentSql,
    periodSqlClause,
    qualifySqlColumn,
    sqlCommentText,
    otpRuleColSql,
    buildLateConditionSql,
    minutesBetweenSql,
    columnComparisonSql,
    COLUMN_COMPARISON_OPERATORS,
    MINUTES_BETWEEN_OPERATORS,
    TODAY_SQL,
    DEFAULT_BLANK_DATE,
    TODAY_COMPARISON_OPERATORS,
    BLANK_DATE_OPERATORS,
    NULL_FALLBACK_OPERATORS,
    isIsoDate,
    nullFallbackType,
    conditionBlankValue,
    nullFallbackFor,
    nullFallbackIssue,
    nullFallbackSupported,
    nullFallbackNote,
    wrapNullFallback,
    leftComparisonSql,
    todayComparisonSql,
    todayPhrase,
};
export type { NullFallback, NullFallbackKind, NullFallbackType };
